import React, { useRef, useEffect, useCallback } from 'react'
import { debounce } from 'lodash'
import ScoopLogo from '../../../../../assets/common/NewLogo.svg'
import { ProcessingSteps } from '../ProcessingSteps/ProcessingSteps'
import { ScoopSpinner, ScoopText } from '../../../../ui'
import styles from './Messages.module.scss'

export const Messages = ({
    // Message data
    convertedMessages,
    hasMoreMessages,
    loadMoreMessages,
    isFetchingMessages,

    // Loading states
    loadingChats,
    loadingChatMessages,
    chatMessagesError,
    isSwitchingChats,

    // Chat state
    currentChat,
    aiStatus,
    processingSteps,

    // UI state
    isCenteredMode,
    isPublic,
    isEmbedded,
    selectedInbox,
    showProcessingSteps,

    // Scroll state
    scrollPositionBeforePagination,
    setScrollPositionBeforePagination,
    lastMessageCount,
    setLastMessageCount,

    // Render function
    renderMessage,
}) => {
    const messagesContainerRef = useRef(null)
    const messagesEndRef = useRef(null)

    // Scroll handler for loading more messages
    const handleScroll = (e) => {
        const container = e.target
        const scrollTop = container.scrollTop
        const threshold = 100 // Load more when within 100px of top

        if (scrollTop <= threshold && hasMoreMessages && !isFetchingMessages) {
            // Save current scroll position before loading more messages
            setScrollPositionBeforePagination({
                scrollTop,
                scrollHeight: container.scrollHeight,
            })
            loadMoreMessages()
        }
    }

    // Debounced scroll handler using lodash
    const debouncedHandleScroll = useCallback(debounce(handleScroll, 200), [
        hasMoreMessages,
        isFetchingMessages,
        loadMoreMessages,
        setScrollPositionBeforePagination,
    ])

    // Handle scroll position restoration after pagination
    useEffect(() => {
        if (scrollPositionBeforePagination && messagesContainerRef.current && !isFetchingMessages) {
            const container = messagesContainerRef.current
            const oldScrollHeight = scrollPositionBeforePagination.scrollHeight
            const newScrollHeight = container.scrollHeight

            // Restore scroll position by accounting for the new content height
            container.scrollTop = newScrollHeight - oldScrollHeight
        }
    }, [
        convertedMessages,
        isFetchingMessages,
        scrollPositionBeforePagination,
        setScrollPositionBeforePagination,
    ])

    // Smart auto-scroll: instant for initial load, smooth for new messages
    useEffect(() => {
        const currentMessageCount = convertedMessages.length || 0

        if (currentMessageCount === 0) {
            // No messages, reset counter
            setLastMessageCount(0)
            return
        }

        // Don't auto-scroll if we're in the middle of pagination
        if (isFetchingMessages || scrollPositionBeforePagination) return

        // Determine if this is initial load vs new messages
        const isInitialLoad = lastMessageCount === 0 && currentMessageCount > 0
        const hasNewMessages = currentMessageCount > lastMessageCount && lastMessageCount > 0

        // Only scroll if we have initial load or new messages (or processing status)
        if (isInitialLoad || hasNewMessages || aiStatus) {
            if (isPublic) {
                // For embedded modes, scroll the container directly
                if (messagesContainerRef.current) {
                    messagesContainerRef.current.scrollTo({
                        top: messagesContainerRef.current.scrollHeight,
                        behavior: isInitialLoad ? 'instant' : 'smooth', // Instant for initial load, smooth for new messages
                    })
                }
            } else {
                // Normal mode: use scrollIntoView
                messagesEndRef.current?.scrollIntoView({
                    behavior: isInitialLoad ? 'instant' : 'smooth', // Instant for initial load, smooth for new messages
                })
            }
        }

        // Update the message count tracker
        setLastMessageCount(currentMessageCount)
    }, [
        convertedMessages,
        aiStatus,
        isPublic,
        lastMessageCount,
        isFetchingMessages,
        scrollPositionBeforePagination,
        setLastMessageCount,
    ])

    return (
        <div
            ref={messagesContainerRef}
            onScroll={debouncedHandleScroll}
            className={styles.container}
        >
            <div className={`${styles.innerContainer} ${isCenteredMode ? styles.centered : ''}`}>
                {loadingChats && !isPublic ? (
                    // Loading state while chats are being loaded
                    <div className={styles.loadingState}>
                        <ScoopSpinner size={32} />
                        <ScoopText className={styles.loadingText}>Loading chats...</ScoopText>
                    </div>
                ) : isCenteredMode && !isPublic ? (
                    // Centered Ask Scoop interface for full experience users
                    <div className={styles.centeredInterface}>
                        <img src={ScoopLogo} alt={'Scoop Logo'} className={styles.logoImage} />
                        <ScoopText className={styles.askScoopTitle}>Ask Scoop</ScoopText>
                        <ScoopText className={styles.askScoopSubtitle}>
                            Click the ? below to explore what's in your data and start asking
                            questions.
                        </ScoopText>
                    </div>
                ) : chatMessagesError && currentChat ? (
                    // Error state when chat message loading fails
                    <div className={styles.errorState}>
                        <ScoopText className={styles.errorTitle}>
                            Failed to load chat messages
                        </ScoopText>
                        <ScoopText className={styles.errorMessage}>
                            There was an error loading the messages for this chat. Please try
                            selecting the chat again.
                        </ScoopText>
                    </div>
                ) : (loadingChatMessages && currentChat && isSwitchingChats) ||
                  (loadingChatMessages && currentChat && convertedMessages.length === 0) ? (
                    // Loading state when switching chats or loading new chat messages
                    <div className={styles.loadingState}>
                        <ScoopSpinner size={32} />
                        <ScoopText className={styles.loadingText}>
                            Loading chat messages...
                        </ScoopText>
                    </div>
                ) : convertedMessages.length > 0 || (aiStatus && processingSteps.length > 0) ? (
                    <>
                        {/* Loading indicator for pagination - only show when loading more (not initial load) */}
                        {isFetchingMessages && hasMoreMessages && convertedMessages.length > 0 && (
                            <div className={styles.paginationLoader}>
                                <ScoopSpinner size={16} />
                                <ScoopText className={styles.paginationText}>
                                    Loading older messages...
                                </ScoopText>
                            </div>
                        )}
                        {/* Render all messages from single source */}
                        {convertedMessages.map((message, index) => renderMessage(message, index))}
                        {aiStatus && processingSteps.length > 0 && (
                            <div className={styles.aiStatusContainer}>
                                {showProcessingSteps ? (
                                    <ProcessingSteps
                                        steps={processingSteps}
                                        isComplete={false}
                                        showSteps={true}
                                    />
                                ) : (
                                    // Show just the current step when toggle is off
                                    <div className={styles.currentStepDisplay}>
                                        <ScoopText className={styles.currentStepText}>
                                            {processingSteps[processingSteps.length - 1]?.message ||
                                                'Processing...'}
                                        </ScoopText>
                                    </div>
                                )}
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <img src={ScoopLogo} alt={'Scoop Logo'} className={styles.emptyLogo} />
                        <ScoopText className={styles.emptyTitle}>Ask Scoop</ScoopText>
                        <ScoopText className={styles.emptyMessage}>
                            {selectedInbox
                                ? 'Type a prompt below to start'
                                : isEmbedded
                                  ? 'Initializing chat...'
                                  : 'Select a dataset from the left panel'}
                        </ScoopText>
                    </div>
                )}
            </div>
        </div>
    )
}
